import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEMO_STATUSES, DEMO_STATUS_LABEL, statusBadge, fmtDateTime } from "@/lib/adminMeta";
import { ArrowLeft, Mail, Phone, Globe, Save } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b border-border last:border-0">
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold sm:w-56 shrink-0">{label}</div>
    <div className="text-sm text-navy break-words">{value || "-"}</div>
  </div>
);

export default function AdminDemoRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [internal, setInternal] = useState({ status: "new", internal_notes: "", next_follow_up: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/demo-requests/${id}`).then(({ data }) => {
      setR(data);
      setInternal({ status: data.status || "new", internal_notes: data.internal_notes || "", next_follow_up: (data.next_follow_up || "").slice(0, 10) });
    }).catch(() => setR(false));
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/demo-requests/${id}`, internal);
      setR(data);
      toast.success("Demo request updated");
    } catch { toast.error("Could not update"); } finally { setSaving(false); }
  };

  if (r === false) return <div className="text-muted-foreground">Demo request not found.</div>;
  if (!r) return <div className="text-muted-foreground">Loading...</div>;
  const website = r.website && !/^https?:\/\//.test(r.website) ? `https://${r.website}` : r.website;

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="admin-demo-detail">
      <button onClick={() => navigate("/admin/demo-requests")} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy" data-testid="demo-detail-back"><ArrowLeft className="w-4 h-4" /> Back to Demo Requests</button>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold text-navy">{r.name || "Demo Request"}</h1>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusBadge(r.status || "new")}`}>{DEMO_STATUS_LABEL[r.status || "new"]}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {r.email && <a href={`mailto:${r.email}`} data-testid="quick-email"><Button variant="outline" size="sm"><Mail className="w-4 h-4 mr-1.5" /> Email Prospect</Button></a>}
        {r.phone && <a href={`tel:${r.phone}`} data-testid="quick-call"><Button variant="outline" size="sm"><Phone className="w-4 h-4 mr-1.5" /> Call Prospect</Button></a>}
        {website && <a href={website} target="_blank" rel="noreferrer" data-testid="quick-website"><Button variant="outline" size="sm"><Globe className="w-4 h-4 mr-1.5" /> Visit Website</Button></a>}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-2xl border border-border p-6">
          <h2 className="text-lg font-bold text-navy mb-3">Submitted Information</h2>
          <Row label="Dealership Name" value={r.dealership_name} />
          <Row label="Email" value={r.email} />
          <Row label="Phone" value={r.phone} />
          <Row label="Number of Locations" value={r.num_locations} />
          <Row label="Active Inventory Size" value={r.inventory_size} />
          <Row label="DMS Used" value={r.dms_used} />
          <Row label="Website" value={r.website} />
          <Row label="Message" value={r.message} />
          <Row label="Submitted" value={fmtDateTime(r.created_at)} />
          <Row label="Last Updated" value={fmtDateTime(r.updated_at)} />
        </div>

        <div className="bg-white rounded-2xl border border-border p-6 h-fit">
          <h2 className="text-lg font-bold text-navy mb-4">Internal (Admin Only)</h2>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={internal.status} onValueChange={(v) => setInternal((s) => ({ ...s, status: v }))}>
                <SelectTrigger data-testid="demo-detail-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{DEMO_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Next Follow-Up Date</Label>
              <Input type="date" value={internal.next_follow_up} onChange={(e) => setInternal((s) => ({ ...s, next_follow_up: e.target.value }))} data-testid="demo-detail-followup" />
            </div>
            <div className="space-y-1.5">
              <Label>Internal Notes</Label>
              <Textarea rows={5} value={internal.internal_notes} onChange={(e) => setInternal((s) => ({ ...s, internal_notes: e.target.value }))} placeholder="Notes visible only to admins" data-testid="demo-detail-notes" />
            </div>
            {internal.status === "converted" && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                Marked as Converted. Link the resulting dealership in the Dealer Accounts section if applicable.
              </div>
            )}
            <Button onClick={save} disabled={saving} className="w-full bg-navy hover:bg-navy-light text-white font-bold" data-testid="demo-detail-save"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
